import { checkJobPayPatch } from '../jobpay.js';
// Project + section commands. Pure; see tasks.js for the ctx shape.
import { del, set, update } from './writes.js';
import { projectStatus, projectStatusPatch } from '../projectstatus.js';
import { JOB_TYPES } from '../profit.js';
import { withTag } from '../tags.js';
import { moveTasksToDepartment, isDepartmentLocked } from '../taskdepartments.js';
import { GENERAL_DEPARTMENT_ID } from '../hierarchy.js';
import { tasksOffDepartment } from '../projectsettings.js';
import { cleanAutomations } from '../processes.js';
import { closePreview, projectTaskIds, isUnfinished, voidPatch, voidedWith, restorePatch, restoreTaskWrites } from '../projectclose.js';
import { bankable } from '../timer.js';
import { stopTask, buildTimeEntry } from './tasks.js';

const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());
const needProject = (ctx, id) => { const p = (ctx.projects || []).find((x) => x.id === id); if (!p) throw new Error(`no project ${id}`); return p; };

/** The one shape of a new project doc (ProjectContext.addProject). */
export function makeProjectDoc(project, at, newId) {
  const id = project.id || newId();
  if (!project.name || !String(project.name).trim()) throw new Error('name required');
  return {
    id, name: String(project.name).trim(), color: project.color || '#3B82F6', icon: project.icon || '📁', description: project.description || '',
    address: project.address || '', addressFromCustomer: !!project.addressFromCustomer,
    customerId: project.customerId || null, customerName: project.customerName || null,
    startDate: project.startDate || null, endDate: project.endDate || null,
    hourlyRate: project.hourlyRate || 0, budget: project.budget || 0, value: project.value || 0,
    billable: project.billable === true || project.billable === false ? project.billable : null,
    status: 'active', departmentId: project.departmentId || null, typeId: project.typeId || null, folderId: project.folderId || null,
    createdAt: at,
  };
}

export function addProject(ctx, input) {
  const project = makeProjectDoc(input, now(ctx), ctx.newId);
  return { writes: [set(`projects/${project.id}`, project)], project };
}

const P_EDITABLE = new Set(['name', 'color', 'icon', 'description', 'address', 'customerId', 'customerName', 'startDate', 'endDate', 'hourlyRate', 'budget', 'value', 'billable', 'status', 'departmentId', 'typeId', 'folderId', 'archived', 'completedAt', 'cancelledAt', 'archivedAt', 'setupSteps', 'cleanupSteps', 'automations',
  'jobType', 'tags', 'bookProjectId', 'booksetId', 'equipment',   // CFO: how it makes money (a label), its tags, the books job it's linked to, tools charged per hour
  'addressFromCustomer', 'newTaskDepartment', 'departmentLock',
  'labourPct', 'billRates', 'travelRate', 'extraRate', 'contractType', 'bidBudget', 'planLockedAt']);   // v0.1.258 how this job pays (jobpay.js)   // departmentLock: every task takes the project's department   // v0.1.226 project settings: the site is the customer's address; new tasks → 'home' department (null = the one you're in)
const DATE = /^\d{4}-\d{2}-\d{2}$/;

export function updateProject(ctx, { id, ...patch }) {
  needProject(ctx, id);
  const data = {};
  for (const [k, v] of Object.entries(patch)) if (P_EDITABLE.has(k) && v !== undefined) data[k] = v;
  if (data.jobType !== undefined && data.jobType !== null && !JOB_TYPES.includes(data.jobType)) throw new Error(`job type must be one of ${JOB_TYPES.join(', ')}`);
  if (data.tags !== undefined) data.tags = (Array.isArray(data.tags) ? data.tags : []).reduce((acc, t) => withTag(acc, t), []);
  if (data.bookProjectId !== undefined && !data.bookProjectId) { data.bookProjectId = null; data.booksetId = null; }   // unlink = both
  if (data.newTaskDepartment !== undefined) data.newTaskDepartment = data.newTaskDepartment === 'home' ? 'home' : null;
  if (data.departmentLock !== undefined) data.departmentLock = data.departmentLock === true ? true : null;
  if (data.automations !== undefined) data.automations = cleanAutomations(data.automations);   // project automations (processes.js)
  if (data.labourPct !== undefined || data.travelRate !== undefined || data.billRates !== undefined || data.extraRate !== undefined) {   // how this job pays (jobpay.js)
    const r = checkJobPayPatch(Object.fromEntries(['labourPct', 'travelRate', 'billRates', 'extraRate'].filter((k) => data[k] !== undefined).map((k) => [k, data[k]])));
    const bad = Object.values(r.errors);
    if (bad.length) throw new Error(bad[0]);
    Object.assign(data, r.patch);
  }
  if (data.name !== undefined && !String(data.name || '').trim()) throw new Error('name required');
  if (data.name !== undefined) data.name = String(data.name).trim();
  for (const k of ['startDate', 'endDate']) {
    if (data[k] === undefined) continue;
    if (!data[k]) data[k] = null;
    else if (!DATE.test(String(data[k]))) throw new Error(`${k} must be YYYY-MM-DD`);
  }
  if (data.startDate !== undefined || data.endDate !== undefined) {
    const p = needProject(ctx, id);
    const start = data.startDate !== undefined ? data.startDate : p.startDate || null;
    const end = data.endDate !== undefined ? data.endDate : p.endDate || null;
    if (start && end && end < start) throw new Error(`the due date ${end} is before the start ${start}`);
  }
  for (const k of ['value', 'budget', 'hourlyRate']) {
    if (data[k] === undefined) continue;
    const n = data[k] === null || data[k] === '' ? 0 : Number(data[k]);
    if (!Number.isFinite(n) || n < 0) throw new Error(`${k} must be a number, 0 or more`);
    data[k] = n;
  }
  if (!Object.keys(data).length) throw new Error('nothing to update');
  data.updatedAt = now(ctx);
  return { writes: [update(`projects/${id}`, data)], patch: data };
}

/**
 * "Apply to all tasks": move the project's open top-level tasks into its home department (department mode —
 * each task still carries its own; this is a one-off sweep, not a link). Returns { writes, moved, skipped }.
 */
export function applyProjectDepartment(ctx, { id }) {
  const p = needProject(ctx, id);
  const ids = tasksOffDepartment(ctx.tasks || [], id, p.departmentId || null, ctx.departments || []);
  return moveTasksToDepartment(ctx, { ids, departmentId: p.departmentId || null });
}

/**
 * Lock projects to a department (or unlock: lock false → global again, tasks keep where they are). Locking sets
 * the department, and moves every top-level task of each project into it in the same group (open and done — the
 * time entries keep the department they ran under), so readers that don't know about the lock agree.
 * ids = one project, or several (e.g. the apps pinned to Dev's Home in one tap). Returns { writes, moved }.
 */
export function lockProjectsDepartment(ctx, { ids = [], departmentId, lock = true }) {
  const at = now(ctx);
  const writes = [];
  let moved = 0;
  for (const id of new Set(ids)) {
    const p = needProject(ctx, id);
    if (!lock) { if (isDepartmentLocked(p)) writes.push(update(`projects/${id}`, { departmentLock: null, updatedAt: at })); continue; }
    const dept = departmentId !== undefined ? (departmentId && departmentId !== GENERAL_DEPARTMENT_ID ? departmentId : null) : p.departmentId || null;
    if (!isDepartmentLocked(p) || (p.departmentId || null) !== dept) writes.push(update(`projects/${id}`, { departmentLock: true, departmentId: dept, updatedAt: at }));
    const ids2 = (ctx.tasks || []).filter((t) => t && t.projectId === id && !t.parentId && !t.deletedAt).map((t) => t.id);
    const m = moveTasksToDepartment({ ...ctx, projects: [] }, { ids: ids2, departmentId: dept });
    writes.push(...m.writes); moved += m.moved;
  }
  return { writes, moved };
}

/**
 * Close a project's unfinished tasks (projectclose.js): every task in it that isn't done or void — todo / paused /
 * blocked, subtasks and setup / cleanup bucket tasks too — becomes `void` in the same group as the status change.
 * The running task (the timer is on it) is stopped first and its time banked, the same way a skip banks it; then it
 * is voided like the rest. No completion stamp, no tags / photo / metric asks, no pay. Dependants outside the
 * project that waited on a voided task are unblocked (what they waited on is gone).
 */
export function closeProjectTasksWrites(ctx, { id, at = now(ctx) }) {
  const pre = closePreview(ctx, id);
  const writes = [];
  let banked = 0;
  if (pre.running) {
    const tid = pre.running.id;
    const b = bankable({ timer: ctx.timer, clockedIn: ctx.clock, taskId: tid, now: at });
    const timeEntry = b.elapsed > 0 && ctx.newId ? buildTimeEntry(b.elapsed, at, ctx.newId) : null;
    const inside = projectTaskIds(ctx.tasks, id);
    const keepLock = !!ctx.lock?.taskId && ctx.lock.taskId !== tid && !inside.has(ctx.lock.taskId);
    writes.push(...stopTask(ctx, { id: tid, elapsed: timeEntry ? b.elapsed : 0, sessionId: ctx.clock?.sessionId ?? null, timeEntry, keepLock }).writes);
    banked = timeEntry ? b.elapsed : 0;
  } else if (ctx.lock?.taskId && pre.tasks.some((t) => t.id === ctx.lock.taskId)) {
    writes.push(set('meta/lock', { active: false, taskId: null }));
  }
  const gone = new Set(pre.tasks.map((t) => t.id));
  for (const t of pre.tasks) writes.push(update(`tasks/${t.id}`, voidPatch(t, id, at)));
  for (const t of ctx.tasks || []) {
    if (t.blockedByTaskId && gone.has(t.blockedByTaskId) && !gone.has(t.id) && isUnfinished(t)) writes.push(update(`tasks/${t.id}`, { status: t.status === 'blocked' ? 'todo' : t.status, blockedByTaskId: null, updatedAt: at }));
  }
  return { writes, voided: pre.tasks.length, running: pre.running ? pre.running.id : null, banked };
}

/** Restore the tasks a project close voided (reopen → "Restore the N voided tasks"). */
export function restoreProjectTasksWrites(ctx, { id, at = now(ctx) }) {
  const list = voidedWith(ctx.tasks, id);
  return { writes: list.map((t) => update(`tasks/${t.id}`, restorePatch(t, at))), restored: list.length };
}

/**
 * Move a project to active / on_hold / completed / cancelled / archived (shared/projectstatus.js).
 * → completed: its unfinished tasks are voided in the same group ({ voided, running, banked }).
 *   leaving completed with `restore: true`: the tasks it voided come back ({ restored }); without it they stay void.
 *   cancelled / on hold / archived: tasks are left as they are.
 * `ctx.tasks` (with the void ones), `ctx.timer`, `ctx.clock`, `ctx.lock` are read when they matter.
 */
export function setProjectStatus(ctx, { id, status, restore = false }) {
  const p = needProject(ctx, id);
  const from = projectStatus(p);
  if (from === status) throw new Error(`${p.name} is already ${status.replace('_', ' ')}`);
  const at = now(ctx);
  const writes = [update(`projects/${id}`, projectStatusPatch(status, at))];
  const out = { voided: 0, running: null, banked: 0, restored: 0 };
  if (status === 'completed') {
    const c = closeProjectTasksWrites(ctx, { id, at });
    writes.push(...c.writes);
    Object.assign(out, { voided: c.voided, running: c.running, banked: c.banked });
  } else if (restore) {
    const r = restoreProjectTasksWrites(ctx, { id, at });
    writes.push(...r.writes);
    out.restored = r.restored;
  }
  return { writes, ...out };
}

/** Completed (closes its tasks — setProjectStatus), or reopen → active (`restore` brings the voided tasks back). */
export function completeProject(ctx, { id, reopen = false, restore = false }) {
  const p = needProject(ctx, id);
  if (!reopen && projectStatus(p) === 'completed') throw new Error(`${p.name} is already completed`);
  if (reopen && projectStatus(p) === 'active') throw new Error(`${p.name} is already active`);
  return setProjectStatus(ctx, { id, status: reopen ? 'active' : 'completed', restore });
}

/** Bring one void task back by hand (its page), with its void subtasks and parents. */
export function restoreVoidTask(ctx, { id }) {
  return restoreTaskWrites(ctx, { id, at: now(ctx) });
}

/** Hard delete of the project doc only (its tasks stay, orphaned — the app does the same). */
export function deleteProject(ctx, { id }) {
  needProject(ctx, id);
  return { writes: [del(`projects/${id}`)] };
}

// ---- sections (phases) ------------------------------------------------------------------

export function addSection(ctx, { projectId, title, description = '', status = 'active', color = null, order = null }) {
  needProject(ctx, projectId);
  if (!title || !String(title).trim()) throw new Error('title required');
  const mine = (ctx.sections || []).filter((s) => s.projectId === projectId);
  const at = now(ctx);
  const section = { id: ctx.newId(), projectId, title: String(title).trim(), description, order: order ?? mine.reduce((m, s) => Math.max(m, Number(s.order) || 0), 0) + 1, status, color, completedAt: null, createdAt: at };
  return { writes: [set(`sections/${section.id}`, section)], section };
}

const S_EDITABLE = new Set(['title', 'description', 'order', 'status', 'color', 'completedAt']);

export function updateSection(ctx, { id, ...patch }) {
  if (!(ctx.sections || []).some((s) => s.id === id)) throw new Error(`no section ${id}`);
  const data = {};
  for (const [k, v] of Object.entries(patch)) if (S_EDITABLE.has(k) && v !== undefined) data[k] = v;
  if (!Object.keys(data).length) throw new Error('nothing to update');
  return { writes: [update(`sections/${id}`, data)], patch: data };
}
